"use client";

import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";

import { usePublicProfileStore } from "../store/usePublicProfileStore";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import PATHS from "@/routes/paths";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";

export function PublicReviewsPreview() {
  const { profileData, reviewsData } = usePublicProfileStore();
  const { t } = useTranslation(["profile"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const { locale, profileId } = useParams();
  const previewReviews = reviewsData?.data.slice(0, 5) || [];

  if (!profileData) return null;

  return (
    <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-amber-400/50 shadow-2xl backdrop-blur-xl xl:max-w-[23rem]">
      {/* Background Pattern */}
      <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 opacity-10" />
      <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-orange-400 to-amber-500 opacity-10" />
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl bg-amber-500/20 p-2">
            <Icon
              className="text-amber-400" // Changed from text-muted-foreground
              height="1.5rem"
              icon="mdi:star-circle-outline"
              width="1.5rem"
            />
          </div>
          {t("reviewsSection.title")}
        </motion.h2>
      </CardHeader>
      <CardContent className="space-y-4">
        {previewReviews.length > 0 ? (
          previewReviews.map((review, index) => (
            <motion.div
              key={review.id}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-amber-300/20 bg-amber-500/10 p-4 backdrop-blur-sm"
              initial={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="flex items-start gap-3">
                <Avatar className="h-10 w-10 border">
                  <AvatarImage
                    alt={review.user.name}
                    src={review.user.avatar || "/placeholder.svg"}
                  />
                  <AvatarFallback>{review.user.name.substring(0, 2)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">{review.user.name}</h3>
                    <StarRating rating={review.rating} size="sm" />
                  </div>
                  <p className="text-muted-foreground text-sm">{review.content}</p>
                  <p className="text-muted-foreground text-xs">
                    {format(review.created_at, "PPPp", {
                      locale: getDateFnsLocale(
                        getLanguageFromLocale(profileData?.header_section.locale_code),
                      ),
                    })}
                  </p>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <p className="text-muted-foreground">{t("reviewsSection.noReviews")}</p>
        )}

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="pt-2"
          initial={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <Button
            asChild
            className="z-50 w-full rounded-xl border-amber-400/30 bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
            variant="outline"
          >
            <Link
              className="flex items-center justify-center gap-2"
              to={`${PATHS.PROFILES.TUTOR_PROFILE}/reviews/${profileId}${locale !== undefined ? `/${locale}` : ""}`}
            >
              <span>{t("reviewsSection.viewAll")}</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </CardContent>
    </Card>
  );
}
